import { Skeleton, Typography, Paper, useTheme, Box, Tabs, Tab, Stack, Tooltip, IconButton } from '@mui/material';

import Iconify from 'src/components/iconify';

import { useSelector } from 'react-redux';
import { useEffect, useMemo, useRef, useState } from 'react';
import { RootState } from 'src/context/store';
import { GridFilterModel } from '@mui/x-data-grid';
import { GridSortModel } from '@mui/x-data-grid';
import { useDataQuery, useKpisQuery } from 'src/context/api/data/api';
import { useExportMutation } from 'src/context/api/exports/api';
import { useCurrentWorkspace } from 'src/context/reducers/app-settings';
import { useLocales } from 'src/locales';
import GenericDataGrid from 'src/components/data-grid/generic-data-grid';
import { GridColumnVisibilityModel, useGridApiRef, GridPreferencePanelsValue } from '@mui/x-data-grid-pro';
import { EXPLORE_COLORS, GRID_HEIGHT, GRID_DEFAULT_METRICS, GRID_BAR_TYPES, GRID_BAR_COLORS, GRID_COLUMN_ORDER, buildExploreBaseTabs } from './constants';

import 'src/components/data-grid/data-grid.scss';
import { enqueueSnackbar } from 'notistack';

export default function ExploreTable() {
  const { t } = useLocales();
  const theme = useTheme();
  const workspace = useCurrentWorkspace();
  const filterStore = useSelector((state: RootState) => state.filters);
  const loadedPlatforms = useSelector((state: RootState) => state.filters.loadedPlatform);
  const mode = useSelector((state: RootState) => state.filters.extraOptions.mode);
  const [value, setValue] = useState(0);
  const [showEvolution, setShowEvolution] = useState(true);
  const gridApiRef = useGridApiRef();
  const columnsBtnRef = useRef<HTMLButtonElement>(null);
  const filterBtnRef = useRef<HTMLButtonElement>(null);
  // La ClickAwayListener du panneau se ferme sur pointerup/mouseup (avant le click).
  // On mémorise donc l'état d'ouverture au mousedown pour un vrai toggle au click.
  const columnsWasOpenRef = useRef(false);
  const filterWasOpenRef = useRef(false);
  // Anchor for the columns/filter panel popover: set to the clicked button so the panel opens under
  // it (the buttons live in this custom header, outside the grid, so MUI can't anchor on its own).
  const [panelAnchor, setPanelAnchor] = useState<HTMLElement | null>(null);
  const [paginationModel, setPaginationModel] = useState({
    pageSize: 20,
    page: 0,
  });
  const [sortModel, setSortModel] = useState<GridSortModel>([
    {
      field:
        value === 3
          ? filterStore?.timestep.toLowerCase().replace('date_day', 'date')
          : 'brand_searches',
      sort: 'desc',
    },
  ]);
  const [filters, setFilterModel] = useState<GridFilterModel>({
    items: [],
  });
  const [exportMutation] = useExportMutation();
  const [visibilityModel, setVisibilityModel] = useState<GridColumnVisibilityModel>({});


const tabs = {
    // L'onglet Countries (value 1) n'apparaît que si la sélection courante porte
    // sur plus d'un pays (vue "All" multi-pays). Filtre sur un seul pays => masqué.
    base: buildExploreBaseTabs(filterStore?.timestep, filterStore?.countries),
  };

  // Si l'onglet Countries était sélectionné puis disparaît (passage à un seul pays),
  // on rebascule sur l'onglet Keywords pour éviter un état sans onglet actif.
  const countriesTabShown = tabs.base.some((tab) => tab.value === 1);
  useEffect(() => {
    if (!countriesTabShown && value === 1) {
      setValue(0);
      const defaultField = mode === 'activations' ? 'ad_cost' : 'brand_searches';
      setSortModel([{ field: defaultField, sort: 'desc' }]);
      setFilterModel({ items: [] });
    }
  }, [countriesTabShown, value, mode]);

  useEffect(() => {
    if (
      loadedPlatforms.length > 1 &&
      !tabs[mode as keyof typeof tabs].some((tab) => tab.value === 8)
    ) {
      tabs[mode as keyof typeof tabs].push({
        label: 'dashboard.filters.platforms',
        value: 8,
        groupby: ['platform'],
      });
    } else {
      tabs[mode as keyof typeof tabs] = tabs[mode as keyof typeof tabs].filter(
        (tab) => tab.value !== 8
      );
    }
  }, [loadedPlatforms]);

  const handleChange = (event: any, newValue: any) => {
    setValue(newValue);

    // Reset du sort model lors du changement de tab pour éviter les problèmes de validation
    if (newValue === 3) {
      setSortModel([
        { field: filterStore?.timestep.toLowerCase().replace('date_day', 'date'), sort: 'desc' },
      ]);
    } else {
      const defaultField = mode === 'activations' ? 'ad_cost' : 'brand_searches';
      setSortModel([{ field: defaultField, sort: 'desc' }]);
    }

    // Reset aussi les filtres si nécessaire
    setFilterModel({
      items: [],
    });
  };

  const { data: kpis, isLoading: isFetchingKPIs, error: kpisError } = useKpisQuery();

  const order_by = useMemo(() => {
    if (!sortModel.length) return [];

    return sortModel
      .map(({ field, sort }) => {
        const compareMatch = field.match(/^(.*?)(Compare|Diff)$/);
        if (compareMatch) {
          const baseField = compareMatch[1].toLowerCase();
          const suffix = compareMatch[2];
          if (suffix === 'Compare') return [`compare.${baseField}`, sort];
          if (suffix === 'Diff') return [`diff.${baseField}`, sort];
        }

        const kpi = kpis?.find((k) => k.id === field);

        if (!kpi) return null;

        return [field, sort];
      })
      .filter((item) => !!item) as [string, string][];
  }, [sortModel, kpis]);

  const groupby = useMemo(
    () => tabs[mode as keyof typeof tabs].find((tab) => tab.value === value)?.groupby ?? [],
    [tabs, value, mode]
  );

  const defaultSortModelForTab = useMemo<GridSortModel>(() => {
    if (value === 3) {
      return [
        {
          field: filterStore?.timestep?.toLowerCase().replace('date_day', 'date') ?? 'date',
          sort: 'desc',
        },
      ];
    }
    const defaultField = mode === 'activations' ? 'ad_cost' : 'brand_searches';
    return [{ field: defaultField, sort: 'desc' }];
  }, [value, mode, filterStore?.timestep]);

  const queryOpts = useMemo(
    () => ({
      group_by: groupby,
      comparison: filterStore?.compare ? 'true' : 'false',
      start: filterStore?.start,
      end: filterStore?.end,
      interval: filterStore?.timestep.toLowerCase(),
      page_size: paginationModel.pageSize,
      page: paginationModel.page + 1,
      search_terms: filterStore?.keywords ?? [],
      countries: filterStore?.countries ?? [],
      devices: filterStore?.devices ?? [],
      platforms: filterStore?.platforms?.map((platform: any) => platform.toUpperCase()) ?? [],
      start_compare: filterStore?.compare ? filterStore?.compareStart : undefined,
      end_compare: filterStore?.compare ? filterStore?.compareEnd : undefined,
      mode: 'base' as 'base' | 'activations',
      output: 'table' as 'table' | 'charts',
      filters,
      workspace_id: workspace?.id as number,
      extra_options: filterStore?.extraOptions,
      order_by,
      tags: filterStore?.tags?.map((tag: any) => tag.label) ?? [],
    }),
    [
      groupby,
      filterStore?.compare,
      filterStore?.start,
      filterStore?.end,
      filterStore?.timestep,
      paginationModel.pageSize,
      paginationModel.page,
      filterStore?.keywords,
      filterStore?.countries,
      filterStore?.devices,
      filterStore?.platforms,
      filterStore?.compareStart,
      filterStore?.compareEnd,
      filterStore?.extraOptions,
      order_by,
      filterStore?.tags,
    ]
  );

  const {
    data: rawResponse,
    isFetching: isFetchingTableData,
    error: tableDataError,
  } = useDataQuery(queryOpts, {
    skip:
      !filterStore?.timestep ||
      !filterStore?.compareStart ||
      !filterStore?.compareEnd ||
      !kpis?.length ||
      groupby.length === 0,
  });

  // Conserve la dernière réponse chargée. Un changement de filtre/tri/page produit de NOUVEAUX
  // arguments de requête : RTK Query crée alors une nouvelle entrée de cache dont `data` est
  // `undefined` le temps du fetch. Sans rétention, la grille se démonterait (skeleton) à chaque
  // interaction et perdrait son état interne (colonnes ajoutées/retirées). On garde donc l'ancienne
  // réponse affichée sous l'overlay de chargement jusqu'à l'arrivée des nouvelles données.
  const [lastResponse, setLastResponse] = useState<any>(undefined);
  useEffect(() => {
    if (rawResponse) setLastResponse(rawResponse);
  }, [rawResponse]);
  const response = rawResponse ?? lastResponse;
  const { data: tableData, total_rows = 0, query_job_id: queryID } = response ?? {};

  const handleExport = () => {
    const columns = (gridApiRef.current?.getAllColumns?.() ?? []) as {
      field: string;
      headerName?: string;
    }[];
    exportMutation({
      queryId: queryID as string,
      name: `[${workspace?.name}] Traffic details export`,
      columns: Object.keys(visibilityModel).filter(
        (key) => visibilityModel[key as keyof typeof visibilityModel] !== false
      ),
      column_tr: Object.keys(visibilityModel).reduce(
        (acc, key) => {
          acc[key] = columns.find((column) => column.field === key)?.headerName as string;
          return acc;
        },
        {} as Record<string, string>
      ),
      dimensions: groupby,
    })
      .unwrap()
      .then(() => {
        enqueueSnackbar("Export is running in background, you will be notified when it's done", {
          variant: 'success',
        });
      })
      .catch(() => {
        enqueueSnackbar('Error exporting data', { variant: 'error' });
      });
  };

  // Tant que le contenu tient sous GRID_HEIGHT, on laisse MUI dimensionner la grille
  // (autoHeight) : elle épouse les lignes au pixel près, sans vide ni « row fantôme ».
  // Au-delà du plafond, on repasse en hauteur fixe + scroll vertical interne (comportement d'origine).
  // L'estimation ne sert qu'à choisir le mode ; une imprécision près du seuil est sans effet visible.
  const fitsWithoutCap = useMemo(() => {
    const HEADER_H = 36; // columnHeaderHeight
    const ROW_H = 44; // rowHeight
    const FOOTER_H = 52; // footer de pagination
    const rowsCount = Array.isArray(tableData) ? tableData.length : 0;
    return HEADER_H + rowsCount * ROW_H + FOOTER_H <= GRID_HEIGHT;
  }, [tableData]);

  return (
    <Paper
      sx={{
        backgroundColor: theme.palette.mode === 'dark' ? '#212b36' : 'white',
        boxShadow:
          '0px 0px 2px 0px rgba(145, 158, 171, 0.2), 0px 12px 24px -4px rgba(145, 158, 171, 0.12)',
        borderRadius: 2,
        marginTop: 2.5, // 20px gap sous le graphe (Figma)
        marginBottom: 1,
        // v7: l'en-tête est peint via ces variables CSS (containerBackground = colonnes
        // normales, pinnedBackground = colonnes dimension épinglées). Styler
        // `.MuiDataGrid-columnHeaders` n'a aucun effet ici.
        '& .MuiDataGrid-root': {
          '--DataGrid-containerBackground': EXPLORE_COLORS.headerBg,
          '--DataGrid-pinnedBackground': EXPLORE_COLORS.headerBg,
        },
        '& .MuiDataGrid-columnHeaderTitle': { color: EXPLORE_COLORS.labelMuted, fontSize: 14 },
        // Padding horizontal des cellules/en-têtes : 14px (défaut MUI = 10px).
        '& .MuiDataGrid-columnHeader, & .MuiDataGrid-cell': {
          paddingLeft: '14px',
          paddingRight: '14px',
        },
        '& .MuiDataGrid-cell': {
          color: EXPLORE_COLORS.valueDark,
          fontSize: 14,
          // Séparateurs pleins (les défauts MUI apparaissent pointillés) — #919EAB @ 20%.
          borderBottom: `1px solid ${EXPLORE_COLORS.gridBorder}`,
          borderRight: `1px solid ${EXPLORE_COLORS.gridBorder}`,
        },
        '& .MuiDataGrid-columnHeader': {
          borderRight: `1px solid ${EXPLORE_COLORS.gridBorder}`,
        },
        // Hover sur TOUTE la ligne (y compris la colonne search term épinglée, dont le fond blanc
        // !important masque sinon le survol natif — cf. .MuiDataGrid-cell--pinnedLeft du grid partagé).
        '& .MuiDataGrid-row:hover': {
          backgroundColor: EXPLORE_COLORS.rowHover,
        },
        '& .MuiDataGrid-row:hover .MuiDataGrid-cell--pinnedLeft': {
          backgroundColor: `${EXPLORE_COLORS.rowHover} !important`,
        },
        '& .MuiDataGrid-footerContainer': {
          backgroundColor: EXPLORE_COLORS.footerBg,
          minHeight: '40px',
          height: '40px',
          '& .MuiTablePagination-root, & .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows, & .MuiTablePagination-select, & .MuiTablePagination-input': {
            color: EXPLORE_COLORS.textDisabled,
            fontSize: 14,
          },
          '& .MuiToolbar-root': { minHeight: '40px' },
        },
      }}
    >
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          px: '20px',
          borderBottom: `1px solid ${EXPLORE_COLORS.borderNeutral}`,
        }}
      >
        <Tabs
          TabIndicatorProps={{
            sx: { backgroundColor: EXPLORE_COLORS.tabActive, height: '2px' },
          }}
          value={value}
          onChange={handleChange}
          aria-label={t('dashboard.brandTraffic.basic_tabs_example')}
          sx={{
            minHeight: 48,
            '& .MuiTab-root': {
              minHeight: 48,
              minWidth: 'auto',
              py: 0,
              px: 0,
              // Le thème global (overrides/components/tabs.tsx) impose marginRight 40px ;
              // on le ramène à 24px entre onglets.
              '&:not(:last-of-type)': { marginRight: '24px' },
              textTransform: 'none',
              fontWeight: 600,
              fontSize: 14,
              color: EXPLORE_COLORS.textDisabled,
            },
            '& .Mui-selected': { color: `${EXPLORE_COLORS.tabActive} !important` },
          }}
        >
          {tabs[mode as keyof typeof tabs].map((tab) => (
            <Tab key={tab.value} value={tab.value} label={t(tab.label)} />
          ))}
        </Tabs>
        <Stack direction="row" alignItems="center" spacing={1}>
          {filterStore?.compare && value !== 3 && (
            <Tooltip
              title={
                showEvolution
                  ? 'Showing % evolution — click for absolute difference'
                  : 'Showing absolute difference — click for % evolution'
              }
              placement="top"
            >
              <Box
                onClick={() => setShowEvolution((v) => !v)}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '2px',
                  px: '6px',
                  py: '4px',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  userSelect: 'none',
                  bgcolor: showEvolution ? 'rgba(145,158,171,0.08)' : 'transparent',
                  color: showEvolution ? '#1e88e5' : '#919EAB',
                }}
              >
                <Iconify
                  icon={showEvolution ? 'material-symbols:percent-rounded' : 'material-symbols:tag-rounded'}
                  width={16}
                  sx={{ color: 'inherit' }}
                />
                <Typography
                  sx={{
                    fontSize: 14,
                    fontWeight: 500,
                    lineHeight: '16.8px',
                    color: 'inherit',
                    whiteSpace: 'nowrap',
                    mx: '2px',
                  }}
                >
                  evolution
                </Typography>
                <Iconify icon="mi:switch" width={16} sx={{ color: 'inherit' }} />
              </Box>
            </Tooltip>
          )}
          <Tooltip title="Show/hide columns" placement="top">
            <IconButton
              ref={columnsBtnRef}
              size="small"
              sx={{ width: 26, height: 26, padding: '4px', color: EXPLORE_COLORS.textDisabled }}
              onMouseDown={() => {
                // Capturé avant que la ClickAwayListener ne referme le panneau.
                const panel = gridApiRef.current?.state?.preferencePanel;
                columnsWasOpenRef.current = !!(panel?.open && panel?.openedPanelValue === GridPreferencePanelsValue.columns);
              }}
              onClick={() => {
                // Re-clic sur l'icône => le panneau a déjà été fermé par la ClickAwayListener, on le laisse fermé (toggle).
                if (columnsWasOpenRef.current) {
                  gridApiRef.current?.hidePreferences?.();
                  return;
                }
                setPanelAnchor(columnsBtnRef.current);
                gridApiRef.current?.showPreferences?.(GridPreferencePanelsValue.columns);
              }}
            >
              <Iconify icon="mingcute:calendar-week-line" width={18} />
            </IconButton>
          </Tooltip>
          <Tooltip title="Filters" placement="top">
            <IconButton
              ref={filterBtnRef}
              size="small"
              sx={{ width: 26, height: 26, padding: '4px', color: EXPLORE_COLORS.textDisabled }}
              onMouseDown={() => {
                // Capturé avant que la ClickAwayListener ne referme le panneau.
                const panel = gridApiRef.current?.state?.preferencePanel;
                filterWasOpenRef.current = !!(panel?.open && panel?.openedPanelValue === GridPreferencePanelsValue.filters);
              }}
              onClick={() => {
                // Re-clic sur l'icône => le panneau a déjà été fermé par la ClickAwayListener, on le laisse fermé (toggle).
                if (filterWasOpenRef.current) {
                  gridApiRef.current?.hidePreferences?.();
                  return;
                }
                setPanelAnchor(filterBtnRef.current);
                gridApiRef.current?.showFilterPanel?.();
              }}
            >
              <Iconify icon="ic:round-filter-list" width={18} />
            </IconButton>
          </Tooltip>
          <Tooltip title="Export" placement="top">
            <IconButton
              size="small"
              sx={{ width: 26, height: 26, padding: '4px', color: EXPLORE_COLORS.textDisabled }}
              onClick={() => {
                if (queryID) handleExport();
              }}
            >
              <Iconify icon="lets-icons:export" width={16} />
            </IconButton>
          </Tooltip>
        </Stack>
      </Box>

      <Box>
        {/* Le squelette ne couvre QUE le tout premier chargement (aucune donnée encore). Un refetch
            d'arrière-plan (filtre, tri, pagination…) NE DOIT PAS démonter la grille : cela détruirait
            son état interne (colonnes ajoutées/retirées) et le réinitialiserait aux défauts. On garde
            donc la grille montée et on signale le chargement via son overlay (`loading`). */}
        {!tableData && !tableDataError ? (
          <Skeleton
            width={'100%'}
            height={GRID_HEIGHT}
            style={{ borderRadius: 0, borderBottomLeftRadius: 10, borderBottomRightRadius: 10 }}
            variant="rectangular"
            animation={'wave'}
          >
            <Iconify py={'auto'} icon={'eva:checkmark-circle-2-fill'} color={'success.main'} />
            <Typography color={'success.main'}>Active</Typography>
          </Skeleton>
        ) : (
          tableData && (
            <Box height={'auto'}>
              <GenericDataGrid
                apiRef={gridApiRef}
                loading={isFetchingTableData}
                hideToolbar
                pinnedColumns={groupby ?? []}
                defaultVisibleFields={GRID_DEFAULT_METRICS}
                columnOrder={GRID_COLUMN_ORDER}
                metricBarTypes={GRID_BAR_TYPES}
                metricBarColors={GRID_BAR_COLORS}
                columnLabelOverrides={{ total_brand_traffic: 'Brand Traffic' }}
                brandTrafficMode="organic-search-shopping"
                persistColumnVisibility
                dimensionFilterField={value === 0 ? 'search_term' : undefined}
                inlineCompare
                evolutionMode={showEvolution}
                gridHeight={fitsWithoutCap ? undefined : GRID_HEIGHT}
                columnHeaderHeight={36}
                rowHeight={44}
                leftAlign
                showDimensionFlags
                data={tableData as any}
                paginationModel={paginationModel}
                onPaginationModelChange={setPaginationModel}
                paginationMode="server"
                rowCount={total_rows}
                sortModel={sortModel}
                onSortModelChange={(model) => {
                  if (model.length === 0) {
                    setSortModel(defaultSortModelForTab);
                  } else {
                    setSortModel(model as GridSortModel);
                  }
                }}
                filterModel={filters}
                onFilterModelChange={setFilterModel}
                onVisibilityModelChange={setVisibilityModel}
                visibilityModel={visibilityModel}
                onExport={handleExport}
                panelAnchorEl={panelAnchor}
              />
            </Box>
          )
        )}
      </Box>
    </Paper>
  );
}
